import { getDateFromBackendISO } from 'utils/dates';
import { getSrcUserCircleAvatar } from 'utils/getSrcAvatar';

const EMPTY_VALUE = '-';

function formatCount(value) {
    if (value == null || value === '') {
        return EMPTY_VALUE;
    }

    const count = Number(value);
    return Number.isFinite(count) ? String(count) : EMPTY_VALUE;
}

function normalizeAuditUser(user) {
    if (!user) {
        return { id: null, name: '' };
    }

    if (typeof user === 'string') {
        return { id: null, name: user };
    }

    const id = user.id ?? user.userId ?? user.idUser ?? null;
    const name = user.description ?? user.name ?? user.userName ?? user.user ?? '';

    return { id, name };
}

function formatCreationDate(isoDate) {
    if (!isoDate || typeof isoDate !== 'string' || !isoDate.trim()) {
        return EMPTY_VALUE;
    }

    return getDateFromBackendISO(isoDate, 'L') || EMPTY_VALUE;
}

function getModifiedByUser(audit) {
    const modifiedUser = audit?.modified?.user;
    const createdUser = audit?.created?.user;

    const hasModifiedUser =
        modifiedUser &&
        (modifiedUser.id || modifiedUser.name || modifiedUser.description || modifiedUser.userName);

    return normalizeAuditUser(hasModifiedUser ? modifiedUser : createdUser);
}

/**
 * Map a single Entity Collection API item to a Marketing Lists table row.
 * Preserves API field names (description, observations, accountCount, …) and adds display fields.
 * @param {object} collection
 * @returns {object}
 */
export function mapMarketingListRow(collection = {}) {
    const id = collection.id ?? collection.collectionId ?? collection.CollectionId ?? null;
    const description = String(collection.description ?? collection.descripcion ?? '').trim();
    const accountCount = collection.accountCount ?? collection.AccountCount;
    const contactCount = collection.contactCount ?? collection.ContactCount;
    const audit = collection.audit ?? collection.Audit;
    const modifiedUser = getModifiedByUser(audit);
    const { src, fallbackSrc } = getSrcUserCircleAvatar(modifiedUser.id);

    return {
        ...collection,
        id,
        description,
        name: description,
        listId: id != null ? `#${id}` : EMPTY_VALUE,
        accounts: formatCount(accountCount),
        contacts: formatCount(contactCount),
        created: formatCreationDate(audit?.created?.date),
        modified: audit?.modified?.date ?? null,
        modifiedById: modifiedUser.id,
        modifiedBy: modifiedUser.name || EMPTY_VALUE,
        modifiedBySrc: src,
        modifiedByPlaceholder: fallbackSrc,
    };
}

/**
 * @param {object[]} collections
 * @returns {object[]}
 */
export function mapMarketingListRows(collections = []) {
    if (!Array.isArray(collections)) {
        return [];
    }

    return collections.map(mapMarketingListRow);
}
